/**
 * Universal Client Timeline — paginated, virtualized-friendly.
 */
import React, { useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Loader2, History, FileText, Calendar, MessageSquare, StickyNote, Sparkles, Bell, Wand2, AlertCircle, ClipboardList } from "lucide-react";

const EVENT_ICONS = {
  appointment_created:  Calendar,
  appointment_completed: Calendar,
  payment_received:     FileText,
  note_added:           StickyNote,
  document_uploaded:    FileText,
  treatment_completed:  ClipboardList,
  record_updated:       ClipboardList,
  estimate_created:     FileText,
  whatsapp_sent:        MessageSquare,
  reminder_sent:        Bell,
  recall_triggered:     Bell,
  status_changed:       Wand2,
  ai_summary_generated: Sparkles,
};

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const TimelineList = ({ clientId, initial, onChange, t }) => {
  const [items, setItems] = useState(initial || []);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { setItems(initial || []); }, [initial]);

  const loadMore = async () => {
    setLoading(true);
    try {
      const oldest = items[items.length - 1]?.created_at;
      if (!oldest) return;
      const r = await axios.get(`${API}/clients/${clientId}/timeline`, {
        params: { limit: 30, before: oldest },
        withCredentials: true,
      });
      setItems([...items, ...(r.data.items || [])]);
      setNextBefore(r.data.next_before);
    } finally { setLoading(false); }
  };

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 p-10 text-center">
        <History className="w-8 h-8 mx-auto text-slate-400" />
        <p className="font-semibold text-sm mt-3">{t("Nessun evento ancora", "No events yet")}</p>
        <p className="text-xs text-muted-foreground mt-1.5 max-w-sm mx-auto">
          {t("Tutti gli eventi del cliente (note, appuntamenti, documenti, AI) compariranno qui.",
             "All client events (notes, appointments, documents, AI) will appear here.")}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 p-4" data-testid="client-timeline-list">
      <ol className="relative border-l-2 border-violet-100 dark:border-violet-900/40 ml-2 space-y-4">
        {items.map(ev => {
          const Icon = EVENT_ICONS[ev.event_type] || AlertCircle;
          return (
            <li key={ev.event_id} className="ml-5 relative">
              <span className="absolute -left-[33px] top-0 w-7 h-7 rounded-full bg-violet-100 dark:bg-violet-900/40 border-2 border-white dark:border-slate-900 flex items-center justify-center">
                <Icon className="w-3.5 h-3.5 text-violet-600 dark:text-violet-300" />
              </span>
              <div className="flex justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-900 dark:text-white">{ev.title}</p>
                  {ev.description && <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 whitespace-pre-wrap">{ev.description}</p>}
                </div>
                <span className="text-[10px] text-muted-foreground whitespace-nowrap">{fmt(ev.created_at)}</span>
              </div>
            </li>
          );
        })}
      </ol>
      {nextBefore && (
        <Button variant="outline" size="sm" className="mt-4 w-full rounded-full" onClick={loadMore} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("Carica altri eventi", "Load more events")}
        </Button>
      )}
    </div>
  );
};

export default TimelineList;
